// What the count reported, in sentences.
//
// **A raw IssueCode never reaches a reader.** `votes-exceed-roll` is a key in a
// closed union that exists so the compiler can keep the engine's construction
// sites honest; printed on a screen at 9pm it is a puzzle, and a puzzle beside a
// number is how a returning officer talks themselves out of a finding. Every
// code has a sentence in ISSUE_COPY, keyed on the engine's own union so a new
// code reds this package rather than rendering as nothing at all.
//
// The severity is a WORD, never only a colour — "Needs a person before anything
// is declared" against "Recorded" is the whole distinction, and it survives a
// greyscale print, a screenshot and colour blindness. `fatal` and `error` share
// the warn role deliberately: on this screen `--color-danger` means one thing
// only, and that is a motion Synod refused.

import type { JSX } from 'react';
import type { CountIssue } from '@cca/decides-count';
import { ISSUE_COPY, SEVERITY_LABEL } from './view';

interface IssueListProps {
  issues: CountIssue[];
  heading: string;
  /** 2 beside the ballot's own issues, 3 inside an item. No level is skipped on
   *  this screen. */
  level: 2 | 3;
}

export function IssueList({ issues, heading, level }: IssueListProps): JSX.Element | null {
  if (issues.length === 0) return null;
  const Heading = level === 2 ? 'h2' : 'h3';

  return (
    <section className="result-issues">
      <Heading className="result-subheading">{heading}</Heading>
      <ul className="result-issue-list">
        {issues.map((issue, index) => (
          // The index is part of the key because the engine can legitimately
          // raise the same code twice for one item (two Houses, two ties), and
          // the code alone would collide.
          <li className="result-issue" key={`${issue.code}-${String(index)}`}>
            {/* `.pill-neutral` is deliberately NOT used: it is --color-muted on
                --color-border-soft, measured at 4.06:1, which is below AA at
                this size. The local role is the same ground with ink on it. */}
            <span
              className={
                issue.severity === 'warning'
                  ? 'pill result-pill result-pill-recorded'
                  : 'pill pill-warn result-pill'
              }
            >
              {SEVERITY_LABEL[issue.severity]}
            </span>{' '}
            {ISSUE_COPY[issue.code]}
          </li>
        ))}
      </ul>
    </section>
  );
}
